import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Palette, Save, Loader2, RotateCcw, Check } from 'lucide-react';
import { DOMAIN_COLOR_CONFIGS, getCurrentDomain } from '@/lib/siteColors';

function DomainColorCard({ config, settings }) {
  const qc = useQueryClient();
  const existing = settings.find((s) => s.setting_key === config.key);
  const current = (existing && existing.setting_value) || config.default;
  const isCurrentDomain = getCurrentDomain() === config.host;

  const [color, setColor] = useState(current);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setColor(current); }, [current]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      if (existing) {
        await base44.entities.Settings.update(existing.id, { setting_value: color });
      } else {
        await base44.entities.Settings.create({ setting_key: config.key, setting_value: color });
      }
      await qc.invalidateQueries({ queryKey: ['settings'] });
      setMsg('Guardado ✓');
    } catch (e) {
      setMsg('Erro (precisa de admin).');
    } finally { setSaving(false); }
  };

  const dirty = color !== current;

  return (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col border-2 ${isCurrentDomain ? 'border-slate-400' : 'border-transparent'}`}>
      <div className="text-white px-4 py-3 flex items-center justify-between" style={{ background: current }}>
        <div>
          <p className="font-bold text-sm">{config.label}</p>
          <p className="text-xs opacity-80">cor primária</p>
        </div>
        {isCurrentDomain && (
          <span className="text-xs bg-white/25 px-2 py-1 rounded-full">domínio atual</span>
        )}
      </div>
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-12 h-12 rounded-lg border border-slate-300 cursor-pointer bg-white"
            title="Escolher cor" />
          <input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="flex-1 text-xs border border-slate-300 rounded-lg px-2 py-2 text-slate-700 font-mono" />
          <button
            onClick={() => setColor(config.default)}
            className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg transition-colors"
            title="Repor padrão">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: color }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar
          </button>
        </div>
        <div className="flex items-center gap-2 mt-auto">
          <span className="w-4 h-4 rounded-full border border-slate-300" style={{ background: current }} />
          <p className="text-xs text-slate-400">Atual: {current}</p>
        </div>
        {msg && <p className="text-xs text-slate-600 flex items-center gap-1"><Check className="w-3 h-3" />{msg}</p>}
      </div>
    </div>
  );
}

// Per-domain primary color picker. One editable card per domain, mirroring
// the site-image editor pattern. Each domain's color is stored in its own
// Settings key and read back by Layout.jsx for the domain the admin is on.
export default function PrimaryColorPicker() {
  const { data: settings = [] } = useQuery({
    queryKey: ['settings', 'v4'],
    queryFn: () => base44.entities.Settings.list('-created_date', 1000),
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 justify-center mb-4">
        <Palette className="w-5 h-5 text-slate-700" />
        <h2 className="text-lg font-bold text-slate-800">Cor base (primária) — por domínio</h2>
      </div>
      <p className="text-center text-slate-500 text-sm mb-6">Cada domínio tem a sua própria cor primária, aplicada no backoffice quando se navega nesse domínio.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {DOMAIN_COLOR_CONFIGS.map((cfg) => (
          <DomainColorCard key={cfg.host} config={cfg} settings={settings} />
        ))}
      </div>
    </div>
  );
}